/**
 * synth.ts — single source of truth for ALL game audio synthesis.
 *
 * Each sound is defined as a pure "render this graph into the given context"
 * function plus its clip duration. The render functions target the generic
 * BaseAudioContext interface so they work with an OfflineAudioContext, which
 * is how player.ts bakes every sound into a WAV clip once at startup. Nothing
 * here touches a live, speaker-bound AudioContext.
 *
 * Section map:
 *   1. Low-level synth helpers
 *   2. Sound registry (durations + render functions)
 *   3. Battle background-music loop builder
 */

import { COMBO_FULL, COMBO_TRIPLE } from '../../utils/battleBeat';

// ─── 1. Low-level synth helpers ──────────────────────────────────────────────
//
// Envelope note (research: click-free Web Audio synthesis, alemangui.github.io/
// ramp-to-value + MDN AudioParam). A raw oscillator started at full amplitude
// jumps from 0 to `amp` in one sample — a discontinuity the ear hears as a click,
// the tell-tale "cheap procedural" artifact. Every voice below ramps gain up over
// a few ms first (ATTACK_SEC), then exponentially decays (exp decay matches our
// logarithmic loudness perception, so it sounds natural). The attack is clamped
// to a fraction of very short clips so it never eats the whole sound.

/** Click-killing fade-in applied to the start of every voice. */
const ATTACK_SEC = 0.004;
/** Floor the exponential decay ramps toward (can't be 0 for exponential ramps). */
const DECAY_FLOOR = 0.001;

/** Schedule a click-free attack→decay gain envelope on `g` starting at time `t`. */
function envelope(g: GainNode, t: number, durSec: number, amp: number) {
  const attack = Math.min(ATTACK_SEC, durSec * 0.4);
  g.gain.setValueAtTime(DECAY_FLOOR, t);
  g.gain.linearRampToValueAtTime(amp, t + attack);
  g.gain.exponentialRampToValueAtTime(DECAY_FLOOR, t + durSec);
}

/**
 * Anticipation riser (research: reveal = riser → payoff). A voice whose pitch
 * sweeps UP while gain SWELLS from silence to a peak — the build that makes the
 * payoff stinger land. Used only for the evolution reveal.
 */
function riser(
  ac: BaseAudioContext,
  type: OscillatorType,
  freqFrom: number,
  freqTo: number,
  startSec: number,
  durSec: number,
  ampPeak: number,
) {
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = type;
  const t = ac.currentTime + startSec;
  o.frequency.setValueAtTime(freqFrom, t);
  o.frequency.exponentialRampToValueAtTime(freqTo, t + durSec);
  o.connect(g);
  g.connect(ac.destination);
  g.gain.setValueAtTime(DECAY_FLOOR, t);
  g.gain.linearRampToValueAtTime(ampPeak, t + durSec); // swell to the reveal
  g.gain.exponentialRampToValueAtTime(DECAY_FLOOR, t + durSec + 0.05); // quick drop at the cut
  o.start(t);
  o.stop(t + durSec + 0.06);
}

function tone(
  ac: BaseAudioContext,
  type: OscillatorType,
  freq: number,
  startSec: number,
  durSec: number,
  amp: number,
) {
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = type;
  o.frequency.value = freq;
  o.connect(g);
  g.connect(ac.destination);
  const t = ac.currentTime + startSec;
  envelope(g, t, durSec, amp);
  o.start(t);
  o.stop(t + durSec);
}

function sweep(
  ac: BaseAudioContext,
  type: OscillatorType,
  freqFrom: number,
  freqTo: number,
  startSec: number,
  durSec: number,
  amp: number,
) {
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = type;
  const t = ac.currentTime + startSec;
  o.frequency.setValueAtTime(freqFrom, t);
  o.frequency.exponentialRampToValueAtTime(freqTo, t + durSec);
  o.connect(g);
  g.connect(ac.destination);
  envelope(g, t, durSec, amp);
  o.start(t);
  o.stop(t + durSec);
}

function noise(ac: BaseAudioContext, startSec: number, durSec: number, amp: number) {
  const n = (ac.sampleRate * durSec) | 0;
  const buf = ac.createBuffer(1, n, ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource();
  src.buffer = buf;
  const g = ac.createGain();
  src.connect(g);
  g.connect(ac.destination);
  const t = ac.currentTime + startSec;
  envelope(g, t, durSec, amp);
  src.start(t);
}

// Wheel spin matches the wheel animation length (TrainingWheel SPIN_DURATION_MS).
const WHEEL_SPIN_DURATION_SEC = 3.0;

/** Roulette-style ratchet that slows to a stop over the spin duration. */
function renderWheelSpin(ac: BaseAudioContext) {
  const iStart = 0.035;
  const iEnd = 0.45;
  const k = Math.log(iEnd / iStart) / WHEEL_SPIN_DURATION_SEC;
  let t = 0;
  while (t < WHEEL_SPIN_DURATION_SEC - iEnd) {
    tone(ac, 'triangle', 680, t, 0.018, 0.13);
    t += iStart * Math.exp(k * t);
  }
}

// ─── 2. Sound registry ───────────────────────────────────────────────────────

/**
 * Every game sound, keyed by name. `duration` is the clip length in seconds
 * (slightly padded past the last scheduled event so nothing is clipped).
 */
export interface SoundDef {
  duration: number;
  render: (ac: BaseAudioContext) => void;
}

export const SOUND_DEFS = {
  // Battle — result jingles
  /**
   * Victory: ascending C major arpeggio (C5-E5-G5-C6). The four notes overlap
   * into a chord by the end, so amp is kept moderate to avoid a loud square
   * stack (0.22 → 0.18) — celebratory but not jarring.
   */
  win: {
    duration: 0.8,
    render: ac =>
      [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) =>
        tone(ac, 'square', freq, i * 0.11, 0.35, 0.18),
      ),
  },
  /** Defeat: descending minor phrase (G4→Eb4→C4→C3). */
  lose: {
    duration: 1.5,
    render: ac => {
      const notes = [392, 311.13, 261.63, 130.81];
      const offsets = [0, 0.22, 0.44, 0.75];
      const durations = [0.3, 0.3, 0.4, 0.65];
      notes.forEach((freq, i) => tone(ac, 'triangle', freq, offsets[i], durations[i], 0.2));
    },
  },

  // Battle — in-fight sounds
  /** Countdown tick for 3, 2, 1 — sharp metronome click (fires 3× fast). */
  countdownTick: {
    duration: 0.12,
    render: ac => tone(ac, 'sine', 480, 0, 0.07, 0.18),
  },
  /** Countdown hits 0 — two-tone whoosh signals resolution. */
  countdownGo: {
    duration: 0.2,
    render: ac => {
      tone(ac, 'square', 600, 0, 0.05, 0.18);
      tone(ac, 'square', 900, 0.05, 0.1, 0.13);
    },
  },
  /**
   * Player's attack lands on opponent — crisp, decisive hit. Research (impact =
   * noise transient + fast downward pitch sweep): a short bright noise transient
   * reads as "contact", a square sweep 420→130 Hz reads as force. Bright square +
   * higher pitch = "I dealt it", the deliberate counterpart to the darker
   * playerHit below (same recipe transposed down ≈ an octave).
   */
  attackHit: {
    duration: 0.2,
    render: ac => {
      noise(ac, 0, 0.035, 0.18); // sharp contact transient
      sweep(ac, 'square', 420, 130, 0, 0.11, 0.2);
    },
  },
  /**
   * Opponent's attack lands on player — duller, heavier "ouch". Same transient+
   * sweep recipe as attackHit but darker (triangle, not square) and lower/slower
   * (220→70 Hz), so the player instantly hears "received" vs "delivered".
   */
  playerHit: {
    duration: 0.22,
    render: ac => {
      noise(ac, 0, 0.05, 0.14); // softer, lower-feeling transient
      sweep(ac, 'triangle', 220, 70, 0, 0.16, 0.22);
    },
  },
  /**
   * **A full commit landing as a combo** — one sound for the whole beat, baked on the SAME
   * timeline the blows land on (`COMBO_FULL` in `utils/battleBeat.ts`), so it starts with the beat
   * and every hit in it lands on a blow. The CHARGE first: a low hum swelling under a rising
   * whine while the energy gathers. Three quick hits, each a step higher (attackHit's recipe,
   * rising: the count climbs, so does the pitch); a riser through the leap; then the finisher —
   * a long noise crash, a sub drop for weight, a square sweep for force, a bright two-note ring
   * over it — and the thud of the target crashing down.
   */
  comboStrike: {
    duration: COMBO_FULL.beatMs / 1000 + 0.3,
    render: ac => {
      const at = COMBO_FULL.hitMs.map((ms) => ms / 1000);
      const charge = COMBO_FULL.chargeMs / 1000;
      const finisher = (COMBO_FULL.finisherMs ?? COMBO_FULL.hitMs[COMBO_FULL.hitMs.length - 1]) / 1000;
      const land = (COMBO_FULL.landMs ?? COMBO_FULL.releaseMs) / 1000;
      riser(ac, 'sine', 70, 140, 0, charge, 0.18);
      riser(ac, 'sawtooth', 220, 1400, 0, charge, 0.05);
      noise(ac, charge * 0.5, charge * 0.5, 0.04);
      at.slice(0, -1).forEach((t, i) => {
        const pitch = 420 * 1.22 ** i;
        noise(ac, t, 0.03, 0.2);
        sweep(ac, 'square', pitch, pitch / 3, t, 0.08, 0.17);
      });
      const leapFrom = at[at.length - 2] + 0.06;
      riser(ac, 'sawtooth', 180, 1100, leapFrom, finisher - leapFrom, 0.08);
      noise(ac, finisher, 0.22, 0.32);
      sweep(ac, 'sine', 160, 34, finisher, 0.5, 0.4);
      sweep(ac, 'square', 380, 60, finisher, 0.26, 0.22);
      tone(ac, 'triangle', 1046.5, finisher + 0.05, 0.5, 0.1);
      tone(ac, 'triangle', 1568, finisher + 0.09, 0.5, 0.08);
      noise(ac, land, 0.08, 0.16);
      sweep(ac, 'sine', 110, 40, land, 0.2, 0.28);
    },
  },
  /**
   * **Three cards landing as a combo** — three quick hits on `COMBO_TRIPLE`'s timeline, each a
   * step higher, the third heavier with a short ring: a strong round, not the finisher.
   */
  comboStrike3: {
    duration: COMBO_TRIPLE.releaseMs / 1000 + 0.35,
    render: ac => {
      const at = COMBO_TRIPLE.hitMs.map((ms) => ms / 1000);
      at.forEach((t, i) => {
        const pitch = 420 * 1.22 ** i;
        const last = i === at.length - 1;
        noise(ac, t, last ? 0.07 : 0.03, last ? 0.26 : 0.2);
        sweep(ac, 'square', pitch, pitch / 3, t, last ? 0.14 : 0.08, last ? 0.2 : 0.17);
        if (last) {
          sweep(ac, 'sine', 140, 50, t, 0.22, 0.26);
          tone(ac, 'triangle', 1046.5, t + 0.04, 0.25, 0.07);
        }
      });
    },
  },
  /** HP is restored — gentle ascending two-tone chime. */
  heal: {
    duration: 0.4,
    render: ac => {
      tone(ac, 'sine', 440, 0, 0.22, 0.14);
      tone(ac, 'sine', 554, 0.12, 0.22, 0.11);
    },
  },

  // Care — pet action sounds
  /** Meat feed — two deep "blop" chomps. */
  feedMeat: {
    duration: 0.3,
    render: ac => {
      sweep(ac, 'triangle', 280, 180, 0, 0.1, 0.22);
      sweep(ac, 'triangle', 260, 160, 0.13, 0.1, 0.18);
    },
  },
  /** Snack feed — light sweet ascending ping. */
  feedSnack: {
    duration: 0.25,
    render: ac => {
      tone(ac, 'sine', 660, 0, 0.08, 0.14);
      tone(ac, 'sine', 880, 0.07, 0.1, 0.11);
    },
  },
  /** Full bath — three descending bubbly pops with water noise. */
  clean: {
    duration: 0.35,
    render: ac => {
      tone(ac, 'sine', 720, 0, 0.08, 0.15);
      tone(ac, 'sine', 600, 0.09, 0.08, 0.12);
      tone(ac, 'sine', 480, 0.18, 0.1, 0.1);
      noise(ac, 0, 0.12, 0.05);
    },
  },
  /** Single poop removed — quick descending pop. */
  partialClean: {
    duration: 0.15,
    render: ac => sweep(ac, 'sine', 420, 200, 0, 0.08, 0.18),
  },

  // Care minigames (Fruit Catch / Rhythm Training / Bubble Scrub)
  /** Minigame begins — playful two-note "ready… go!" pickup. */
  minigameStart: {
    duration: 0.45,
    render: ac => {
      tone(ac, 'square', 523, 0, 0.1, 0.13);
      tone(ac, 'square', 784, 0.16, 0.22, 0.15);
    },
  },
  /** Successful hit (fruit caught / beat on time) — bright short tick. */
  minigameHit: {
    duration: 0.1,
    render: ac => tone(ac, 'sine', 880, 0, 0.06, 0.15),
  },
  /** Bubble popped — watery descending blip, distinct from the dry hit tick. */
  minigamePop: {
    duration: 0.12,
    render: ac => {
      sweep(ac, 'sine', 900, 350, 0, 0.08, 0.17);
      noise(ac, 0, 0.02, 0.06);
    },
  },
  /** Miss / dropped fruit / off-beat — dull low blip, deliberately soft. */
  minigameMiss: {
    duration: 0.15,
    render: ac => sweep(ac, 'triangle', 220, 130, 0, 0.12, 0.12),
  },
  /** Bronze tier — a single modest note; you got something. */
  minigameTierBronze: {
    duration: 0.35,
    render: ac => tone(ac, 'square', 523, 0, 0.28, 0.13),
  },
  /** Silver tier — two ascending notes. */
  minigameTierSilver: {
    duration: 0.45,
    render: ac => {
      tone(ac, 'square', 523, 0, 0.12, 0.13);
      tone(ac, 'square', 659, 0.13, 0.26, 0.13);
    },
  },
  /** Gold tier — three-note ascending fanfare. */
  minigameTierGold: {
    duration: 0.6,
    render: ac => {
      tone(ac, 'square', 523, 0, 0.11, 0.14);
      tone(ac, 'square', 659, 0.11, 0.11, 0.14);
      tone(ac, 'square', 784, 0.22, 0.3, 0.14);
    },
  },
  /** Perfect tier — full arpeggio + octave shimmer, the run to brag about. */
  minigameTierPerfect: {
    duration: 0.9,
    render: ac => {
      tone(ac, 'square', 523, 0, 0.1, 0.15);
      tone(ac, 'square', 659, 0.1, 0.1, 0.15);
      tone(ac, 'square', 784, 0.2, 0.1, 0.15);
      tone(ac, 'square', 1047, 0.3, 0.32, 0.14);
      tone(ac, 'sine', 2093, 0.34, 0.5, 0.05); // shimmer rings out
    },
  },
  /** Medicine — pure healing ping (528 Hz) with soft overtone. */
  medicine: {
    duration: 0.6,
    render: ac => {
      tone(ac, 'sine', 528, 0, 0.5, 0.16);
      tone(ac, 'sine', 660, 0.1, 0.4, 0.08);
    },
  },
  /** Starting to rest — soft descending lullaby (G4 → E4 → C4). */
  rest: {
    duration: 0.8,
    render: ac => {
      tone(ac, 'triangle', 392, 0, 0.22, 0.13);
      tone(ac, 'triangle', 330, 0.2, 0.22, 0.11);
      tone(ac, 'triangle', 262, 0.4, 0.3, 0.09);
    },
  },
  /** Waking up — quick rising alert (C4 → E4 → G4 → C5). */
  wakeUp: {
    duration: 0.55,
    render: ac => {
      tone(ac, 'triangle', 262, 0, 0.1, 0.15);
      tone(ac, 'triangle', 330, 0.09, 0.1, 0.14);
      tone(ac, 'triangle', 392, 0.18, 0.1, 0.14);
      tone(ac, 'triangle', 523, 0.27, 0.2, 0.12);
    },
  },
  /** Energy refill — electric sawtooth zap. */
  refill: {
    duration: 0.2,
    render: ac => {
      sweep(ac, 'sawtooth', 280, 960, 0, 0.14, 0.14);
      noise(ac, 0, 0.05, 0.1);
    },
  },

  // Care — training wheel sounds
  /** Roulette ratchet slowing to a stop (matches the wheel animation). */
  wheelSpin: {
    duration: WHEEL_SPIN_DURATION_SEC,
    render: renderWheelSpin,
  },
  /** XP bonus — bright three-note ascending chime (C-E-G). */
  wheelResultXP: {
    duration: 0.5,
    render: ac => {
      tone(ac, 'square', 523, 0, 0.12, 0.14);
      tone(ac, 'square', 659, 0.11, 0.12, 0.13);
      tone(ac, 'square', 784, 0.22, 0.2, 0.12);
    },
  },
  /**
   * Prize (XP + Stats) — triumphant five-note square fanfare (C-E-G-C-E across two
   * octaves). Research (casual reward stingers): "big" rewards layer a bright high
   * partial that rings out after the melody — a sine one octave above the final
   * note, longer decay, so the fanfare sparkles rather than stopping dead.
   */
  wheelResultPrize: {
    duration: 0.85,
    render: ac => {
      tone(ac, 'square', 523, 0, 0.1, 0.16);
      tone(ac, 'square', 659, 0.09, 0.1, 0.15);
      tone(ac, 'square', 784, 0.18, 0.1, 0.15);
      tone(ac, 'square', 1047, 0.27, 0.12, 0.14);
      tone(ac, 'square', 1319, 0.38, 0.3, 0.13);
      tone(ac, 'sine', 2637.02, 0.4, 0.42, 0.05); // octave-up shimmer, rings out
    },
  },
  /**
   * Sick result — sad descending minor phrase (G4→Eb4→G3). Research: a faint
   * +15-cent detuned layer adds gentle "roughness"/unease so it reads as *wrong*
   * without the harshness of real dissonance — appropriate for a cozy game's
   * worst-but-not-punishing outcome. Soft triangle throughout.
   */
  wheelResultSick: {
    duration: 0.8,
    render: ac => {
      const DETUNE = 1.00868; // +15 cents
      tone(ac, 'triangle', 392, 0, 0.2, 0.14);
      tone(ac, 'triangle', 392 * DETUNE, 0, 0.2, 0.05);
      tone(ac, 'triangle', 311, 0.18, 0.2, 0.12);
      tone(ac, 'triangle', 196, 0.38, 0.35, 0.1);
    },
  },
  /**
   * Suika reward — coin ding. Research (Mario-coin analysis): the "bling" is an
   * appoggiatura — a fast grace note leaping up into the target across a ~perfect
   * 4th — not a plain sustained tone. Grace B5 (988) → A5 (880) → up a 5th to E6
   * (1319), sine for a clean chime. The quick grace blip is what sells "coin".
   */
  wheelResultSuika: {
    duration: 0.5,
    render: ac => {
      tone(ac, 'sine', 987.77, 0, 0.04, 0.14); // grace note (appoggiatura)
      tone(ac, 'sine', 880.0, 0.04, 0.16, 0.16);
      tone(ac, 'sine', 1318.51, 0.19, 0.26, 0.13);
    },
  },
  /** No bonus — quiet muted thud, understated. */
  wheelResultNothing: {
    duration: 0.2,
    render: ac => tone(ac, 'triangle', 200, 0, 0.15, 0.1),
  },

  // Milestones — evolution, level up, adoption, eggs
  /**
   * Pet evolves / hatches — the showcase reveal, built as anticipation → payoff
   * (research: risers + reveal stingers). ~0.7s riser (saw sweep 200→1200 Hz
   * swelling from silence, plus a high sine shimmer fading in) builds tension;
   * at the reveal (~0.72s) it cuts to a two-octave major arpeggio payoff
   * (C5-E5-G5-C6-E6-G6) on soft triangle, punctuated by a white-noise sparkle and
   * a low sine "boom" for weight. It's clearly bigger and more special than a
   * level-up. Loudest sound in the game (peak of the loudness hierarchy).
   */
  evolve: {
    duration: 1.7,
    render: ac => {
      // Anticipation: rising saw + fading-in high shimmer.
      riser(ac, 'sawtooth', 200, 1200, 0, 0.7, 0.1);
      riser(ac, 'sine', 1600, 2800, 0.15, 0.55, 0.05);
      // Reveal payoff at 0.72s: two-octave arpeggio + sparkle + low boom.
      const REVEAL = 0.72;
      const arp = [523.25, 659.25, 783.99, 1046.5, 1318.51, 1567.98];
      arp.forEach((freq, i) => tone(ac, 'triangle', freq, REVEAL + i * 0.06, 0.5, 0.14));
      noise(ac, REVEAL, 0.18, 0.1); // sparkle burst at the moment of reveal
      tone(ac, 'sine', 1046.5, REVEAL + arp.length * 0.06, 0.5, 0.06); // ring-out shimmer
      tone(ac, 'sine', 82.41, REVEAL, 0.35, 0.18); // low boom = weight
    },
  },
  /**
   * Level up — short, bright, celebratory triple-jump (G5-C6-E6) on square,
   * cheaper/quicker than an evolution so it can fire often without fatigue.
   */
  levelUp: {
    duration: 0.6,
    render: ac => {
      tone(ac, 'square', 783.99, 0, 0.1, 0.15);
      tone(ac, 'square', 1046.5, 0.1, 0.1, 0.15);
      tone(ac, 'square', 1318.51, 0.21, 0.28, 0.14);
    },
  },
  /**
   * Adoption / mint success — a warm, welcoming major arpeggio (C4-E4-G4-C5)
   * on sine with a gentle bell overtone. Softer and rounder than the battle win.
   */
  adoptSuccess: {
    duration: 0.9,
    render: ac => {
      [261.63, 329.63, 392.0, 523.25].forEach((freq, i) =>
        tone(ac, 'sine', freq, i * 0.12, 0.45, 0.16),
      );
      // Soft bell overtone on the resolving note.
      tone(ac, 'sine', 1046.5, 0.36, 0.4, 0.06);
    },
  },
  /**
   * Egg tapped to hatch — a soft "crack" (short noise burst + low pop) resolving
   * into a bright reveal ping. The tactile moment before the reveal shimmer.
   */
  eggHatch: {
    duration: 0.6,
    render: ac => {
      noise(ac, 0, 0.06, 0.16);
      sweep(ac, 'triangle', 300, 160, 0.02, 0.1, 0.18);
      tone(ac, 'sine', 880.0, 0.22, 0.14, 0.13);
      tone(ac, 'sine', 1174.66, 0.34, 0.2, 0.1);
    },
  },
  /**
   * New egg created via breeding — a gentle two-note "pop + sparkle" (E5 → B5),
   * hopeful and light, distinct from the hatch (which has the crack).
   */
  eggCreated: {
    duration: 0.45,
    render: ac => {
      tone(ac, 'sine', 659.25, 0, 0.12, 0.15);
      tone(ac, 'sine', 987.77, 0.13, 0.24, 0.12);
    },
  },

  // Notifications — generic toast + PVP matchmaking
  /** Generic success toast — quick, pleasant two-note rise (C5 → G5). */
  toastSuccess: {
    duration: 0.28,
    render: ac => {
      tone(ac, 'sine', 523.25, 0, 0.09, 0.13);
      tone(ac, 'sine', 783.99, 0.09, 0.16, 0.12);
    },
  },
  /**
   * Generic error toast — soft, low two-note fall (A3 → E3), never harsh.
   * duration 0.34 clears the second note's end (0.13 + 0.18 = 0.31) so the
   * fall isn't cut off.
   */
  toastError: {
    duration: 0.34,
    render: ac => {
      tone(ac, 'triangle', 220.0, 0, 0.12, 0.15);
      tone(ac, 'triangle', 164.81, 0.13, 0.18, 0.13);
    },
  },
  /**
   * PVP opponent found — an attention-getting "ding-dong" (E5 → C5) with a
   * little bell shimmer, clearly a call to action.
   */
  matchFound: {
    duration: 0.55,
    render: ac => {
      tone(ac, 'square', 659.25, 0, 0.16, 0.14);
      tone(ac, 'square', 523.25, 0.18, 0.28, 0.14);
      tone(ac, 'sine', 1318.51, 0.02, 0.2, 0.05);
    },
  },
} satisfies Record<string, SoundDef>;

export type SoundName = keyof typeof SOUND_DEFS;

// ─── 3. Battle background-music loop ─────────────────────────────────────────
//
// A relaxed, melodic battle theme in A-minor pentatonic over the i–VI–III–VII
// progression (Am–F–C–G). Design goals (vs. the old harsh 4s square-wave loop):
//   • Pentatonic melody → every note stays consonant against the chords, so it
//     never sounds grating no matter how long it loops.
//   • Soft triangle lead + sine bass/pad instead of a buzzy square wave.
//   • Gentle per-note attack envelopes remove the click that full-amplitude
//     note starts produced.
//   • An 8-bar A/B structure (≈16s) keeps the loop from feeling repetitive.
//   • Sparser, quieter percussion (kick + backbeat + soft offbeat hat).
//   • Decay tails wrap around the loop boundary, so playback repeats seamlessly
//     with no click at the seam.

const MUSIC_BPM = 116;
const MUSIC_BEAT = 60 / MUSIC_BPM;
const BEATS_PER_BAR = 4;
const MUSIC_BARS = 8;
const MUSIC_LOOP_BEATS = MUSIC_BARS * BEATS_PER_BAR;

/** Total length of one battle-music loop, in seconds. */
export const BATTLE_LOOP_DURATION_SEC = MUSIC_LOOP_BEATS * MUSIC_BEAT;

/** Equal-tempered note frequencies (Hz) used by the battle theme. */
const NOTE = {
  E2: 82.41, F2: 87.31, G2: 98.0, A2: 110.0, B3: 246.94,
  C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.0, A3: 220.0,
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0,
  C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99, A5: 880.0,
} as const;

/** Per-bar chord roots/fifths for the bass line (Am F C G, repeated). */
const BASS_BY_BAR: ReadonlyArray<readonly [number, number]> = [
  [NOTE.A2, NOTE.E3], [NOTE.F2, NOTE.C3], [NOTE.C3, NOTE.G3], [NOTE.G2, NOTE.D3],
  [NOTE.A2, NOTE.E3], [NOTE.F2, NOTE.C3], [NOTE.C3, NOTE.G3], [NOTE.G2, NOTE.D3],
];

/** Per-bar warm pad chord (root/third/fifth) sustained under the melody. */
const PAD_BY_BAR: ReadonlyArray<readonly number[]> = [
  [NOTE.A3, NOTE.C4, NOTE.E4], [NOTE.F3, NOTE.A3, NOTE.C4],
  [NOTE.C4, NOTE.E4, NOTE.G4], [NOTE.G3, NOTE.B3, NOTE.D4],
  [NOTE.A3, NOTE.C4, NOTE.E4], [NOTE.F3, NOTE.A3, NOTE.C4],
  [NOTE.C4, NOTE.E4, NOTE.G4], [NOTE.G3, NOTE.B3, NOTE.D4],
];

/** Lead melody as [startBeat, durationBeats, freq] events (pentatonic arc). */
const MELODY: ReadonlyArray<readonly [number, number, number]> = [
  // A section (bars 1–4): a gentle rise and fall
  [0, 1, NOTE.A4], [1, 1, NOTE.C5], [2, 1, NOTE.E5], [3, 1, NOTE.D5],
  [4, 1, NOTE.D5], [5, 1, NOTE.C5], [6, 2, NOTE.A4],
  [8, 1, NOTE.C5], [9, 1, NOTE.D5], [10, 1, NOTE.E5], [11, 1, NOTE.G5],
  [12, 2, NOTE.E5], [14, 2, NOTE.D5],
  // B section (bars 5–8): climbs higher, then resolves and leaves a breath
  [16, 1, NOTE.E5], [17, 1, NOTE.G5], [18, 2, NOTE.A5],
  [20, 1, NOTE.G5], [21, 1, NOTE.E5], [22, 1, NOTE.D5], [23, 1, NOTE.C5],
  [24, 1, NOTE.E5], [25, 1, NOTE.G5], [26, 1, NOTE.E5], [27, 1, NOTE.D5],
  [28, 2, NOTE.C5], [30, 1, NOTE.A4],
];

const LEAD_AMP = 0.16;
const BASS_AMP = 0.26;
const PAD_AMP = 0.045;
const KICK_AMP = 0.4;
const SNARE_AMP = 0.07;
const HAT_AMP = 0.03;
const NOTE_ATTACK_SEC = 0.006; // short fade-in that kills the note-start click

/**
 * Pre-renders one loop of battle music into a Float32Array. player.ts encodes
 * this into a WAV clip and loops it on an HTMLAudioElement.
 */
export function buildBattleLoop(sr: number): Float32Array {
  const samples = Math.floor(sr * BATTLE_LOOP_DURATION_SEC);
  const d = new Float32Array(samples);

  type Wave = 'sine' | 'triangle';
  const waveform = (type: Wave, phase: number) =>
    type === 'sine'
      ? Math.sin(phase)
      : (2 / Math.PI) * Math.asin(Math.sin(phase)); // soft triangle (few harmonics)

  // Pitched voice with a short attack ramp and exponential decay tail. The tail
  // wraps past the loop end back to the start so the loop is seamless.
  function voice(startSec: number, freq: number, amp: number, decay: number, dur: number, type: Wave) {
    const st = Math.floor(startSec * sr);
    const len = Math.floor(dur * sr);
    const attack = NOTE_ATTACK_SEC * sr;
    for (let s = 0; s < len; s++) {
      const t = s / sr;
      const env = (s < attack ? s / attack : 1) * Math.exp(-decay * t);
      d[(st + s) % samples] += waveform(type, 2 * Math.PI * freq * t) * env * amp;
    }
  }

  function addKick(startSec: number) {
    const f0 = 140,
      k = 28;
    const st = Math.floor(startSec * sr);
    const len = Math.floor(0.2 * sr);
    for (let s = 0; s < len; s++) {
      const t = s / sr;
      const phase = 2 * Math.PI * (f0 / k) * (1 - Math.exp(-k * t));
      d[(st + s) % samples] += Math.sin(phase) * Math.exp(-16 * t) * KICK_AMP;
    }
  }

  function addSnare(startSec: number) {
    const st = Math.floor(startSec * sr);
    const len = Math.floor(0.12 * sr);
    for (let s = 0; s < len; s++) {
      const t = s / sr;
      d[(st + s) % samples] += (Math.random() * 2 - 1) * Math.exp(-32 * t) * SNARE_AMP;
    }
  }

  function addHat(startSec: number) {
    const st = Math.floor(startSec * sr);
    const len = Math.floor(0.03 * sr);
    for (let s = 0; s < len; s++)
      d[(st + s) % samples] += (Math.random() * 2 - 1) * Math.exp(-120 * (s / sr)) * HAT_AMP;
  }

  // Lead melody (soft triangle).
  for (const [startBeat, durBeats, freq] of MELODY)
    voice(startBeat * MUSIC_BEAT, freq, LEAD_AMP, 3.2, durBeats * MUSIC_BEAT * 0.96, 'triangle');

  // Per-bar bass (root then fifth) + sustained warm pad chord.
  for (let bar = 0; bar < MUSIC_BARS; bar++) {
    const barSec = bar * BEATS_PER_BAR * MUSIC_BEAT;
    const [root, fifth] = BASS_BY_BAR[bar];
    voice(barSec, root, BASS_AMP, 2.0, MUSIC_BEAT * 1.6, 'sine');
    voice(barSec + 2 * MUSIC_BEAT, fifth, BASS_AMP, 2.0, MUSIC_BEAT * 1.6, 'sine');
    for (const padFreq of PAD_BY_BAR[bar])
      voice(barSec, padFreq, PAD_AMP, 0.6, BEATS_PER_BAR * MUSIC_BEAT, 'sine');
  }

  // Percussion: kick on 1 & 3, soft snare backbeat on 2 & 4, hat on offbeats.
  for (let beat = 0; beat < MUSIC_LOOP_BEATS; beat++) {
    const beatSec = beat * MUSIC_BEAT;
    const beatInBar = beat % BEATS_PER_BAR;
    if (beatInBar === 0 || beatInBar === 2) addKick(beatSec);
    if (beatInBar === 1 || beatInBar === 3) addSnare(beatSec);
    addHat(beatSec + MUSIC_BEAT / 2);
  }

  // Soft-clip the summed mix so stacked voices never hard-clip on encode.
  for (let i = 0; i < samples; i++) d[i] = Math.tanh(d[i]);

  return d;
}
